import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'

import { cx } from './core/classes.js'
import { mergeRenderProps } from './core/render.js'

let didWarnIconPlain = false

export function Button(props: Button.Props) {
  const {
    children,
    className,
    disabled,
    icon,
    loading = false,
    render,
    size = 'medium',
    variant = 'secondary',
    ...rest
  } = props

  const inner = (
    <>
      <span
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute inset-0 rounded-[inherit] border border-transparent',
          Button.chromeClassName[variant],
        )}
        data-button-chrome
      />
      <span className="relative inline-flex items-center justify-center gap-[inherit]">
        {loading ? <SpinnerIcon /> : icon ? <span className="shrink-0">{icon}</span> : null}
        {children}
      </span>
    </>
  )

  const ownProps = {
    className: cx(
      'group relative inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-button border border-transparent font-normal focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-focus data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[loading]:pointer-events-none',
      Button.sizeClassName[size],
      Button.variantClassName[variant],
      className,
    ),
    'data-disabled': disabled ? '' : undefined,
    'data-loading': loading ? '' : undefined,
    ...rest,
  }

  if (render && isValidElement(render))
    return cloneElement(
      render,
      mergeRenderProps(render.props as Record<string, unknown>, ownProps),
      inner,
    )

  return (
    <button {...ownProps} disabled={disabled || loading}>
      {inner}
    </button>
  )
}

export namespace Button {
  export type Size = 'large' | 'medium' | 'small'
  export type Variant = 'negative' | 'primary' | 'secondary'

  export type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
    /** Icon or element before the label. */
    icon?: ReactNode | undefined
    /** Show a loading spinner and disable the button. */
    loading?: boolean | undefined
    /** Element to render as instead of `<button>` (e.g. `<a href="…" />` for navigation). */
    render?: ReactElement | undefined
    /** Button size variant. @default 'medium' */
    size?: Size | undefined
    /** Button visual variant. @default 'secondary' */
    variant?: Variant | undefined
  }

  export const variantClassName = {
    negative: 'text-on-negative',
    primary: 'text-on-accent',
    secondary: 'text-foreground',
  } as const

  export const chromeClassName = {
    negative:
      'bg-negative group-hover:bg-negative-hover group-active:bg-negative-active group-hover:group-active:bg-negative-active',
    primary:
      'bg-accent group-hover:bg-accent-hover group-active:bg-accent-active group-hover:group-active:bg-accent-active',
    secondary:
      'bg-secondary group-hover:bg-secondary-hover group-active:bg-secondary-active group-hover:group-active:bg-secondary-active',
  } as const

  export const sizeClassName = {
    large: 'h-[44px] gap-[8px] px-[22px] button-16',
    medium: 'h-[40px] gap-[8px] px-[16px] button-14',
    small: 'h-[32px] gap-[6px] px-[12px] button-12',
  } as const

  /** Minimal icon-only button for inline actions (copy, dismiss, etc.). */
  export function Icon(props: Icon.Props) {
    const context = useContext(Icon.Context)
    const {
      children,
      className,
      plain = false,
      ref,
      size = 'sm',
      title,
      variant: variantProp,
      ...rest
    } = props
    const variant = plain ? 'plain' : (variantProp ?? context?.variant ?? 'default')
    if (plain) warnIconPlain()

    return (
      <button
        aria-label={rest['aria-label'] ?? title}
        className={cx(
          'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-button text-foreground-secondary focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-focus',
          Icon.variantClassName[variant],
          Icon.sizeClassName[size],
          className,
        )}
        ref={ref}
        type="button"
        {...rest}
      >
        {children}
      </button>
    )
  }

  export namespace Icon {
    export type Size = 'lg' | 'md' | 'sm' | 'xs'
    export type Variant = 'default' | 'plain' | 'surface'

    export type ContextValue = {
      /** Default icon button variant for a subtree. */
      variant?: Variant | undefined
    }

    export const Context = createContext<ContextValue | undefined>(undefined)

    export type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
      /** Render without the filled background. @deprecated Use variant="plain" instead. @default false */
      plain?: boolean | undefined
      /** Button ref. */
      ref?: Ref<HTMLButtonElement> | undefined
      /** Icon button size. @default 'sm' */
      size?: Size | undefined
      /** Accessible label for the icon-only button. */
      title?: string | undefined
      /** Visual style: 'default' for filled icon buttons, 'plain' for transparent icon buttons, or 'surface' for bordered icon buttons. @default 'default' */
      variant?: Variant | undefined
    }

    export const variantClassName = {
      default:
        'bg-secondary hover:bg-secondary-hover hover:text-foreground-secondary-hover active:bg-secondary-active',
      plain:
        'hover:bg-surface-hover hover:text-foreground-secondary-hover active:bg-surface-active active:text-foreground',
      surface:
        'border border-border bg-surface hover:bg-secondary-hover hover:text-foreground active:bg-secondary-active active:text-foreground',
    } as const satisfies Record<Variant, string>

    export const sizeClassName = {
      lg: 'h-[32px] w-[32px] [&>svg]:h-[16px] [&>svg]:w-[16px]',
      md: 'h-[32px] w-[32px] [&>svg]:h-[14px] [&>svg]:w-[14px]',
      sm: 'h-[24px] w-[24px] [&>svg]:h-[12px] [&>svg]:w-[12px]',
      xs: 'h-[20px] w-[20px] [&>svg]:h-[10px] [&>svg]:w-[10px]',
    } as const
  }

  export function Text(props: Text.Props) {
    const {
      children,
      className,
      disabled,
      prefix,
      render,
      size = 'medium',
      suffix,
      ...rest
    } = props

    const inner = (
      <>
        {prefix && <span className="shrink-0">{prefix}</span>}
        {children}
        {suffix && <span className="shrink-0">{suffix}</span>}
      </>
    )

    const ownProps = {
      className: cx(
        'inline-flex shrink-0 cursor-pointer items-center justify-center gap-[4px] whitespace-nowrap rounded-button font-normal text-foreground-secondary hover:bg-surface-hover hover:text-foreground-secondary-hover active:bg-surface-active active:text-foreground [&:hover:active]:bg-surface-active [&:hover:active]:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-focus data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        Text.sizeClassName[size],
        className,
      ),
      'data-disabled': disabled ? '' : undefined,
      ...rest,
    }

    if (render && isValidElement(render))
      return cloneElement(
        render,
        mergeRenderProps(render.props as Record<string, unknown>, ownProps),
        inner,
      )

    return (
      <button {...ownProps} disabled={disabled}>
        {inner}
      </button>
    )
  }

  export namespace Text {
    export type Size = 'large' | 'medium' | 'small'

    export type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'> & {
      /** Icon or element before the label. */
      prefix?: ReactNode | undefined
      /** Element to render as instead of `<button>` (e.g. `<a href="…" />` for navigation). */
      render?: ReactElement | undefined
      /** Text button size. @default 'medium' */
      size?: Size | undefined
      /** Icon or element after the label. */
      suffix?: ReactNode | undefined
    }

    export const sizeClassName = {
      large: 'px-[6px] py-[3px] label-14',
      medium: 'px-[6px] py-[3px] label-13',
      small: 'px-[5px] py-[2px] label-12',
    } as const
  }
}

function SpinnerIcon() {
  return (
    <svg className="h-[16px] w-[16px] animate-rgn-spin" viewBox="0 0 16 16" fill="none" aria-hidden>
      {Array.from({ length: 12 }, (_, i) => (
        <line
          key={i}
          x1="8"
          y1="1.5"
          x2="8"
          y2="4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity={0.15 + (i / 12) * 0.85}
          transform={`rotate(${i * 30} 8 8)`}
        />
      ))}
    </svg>
  )
}

function warnIconPlain() {
  if (didWarnIconPlain) return
  didWarnIconPlain = true
  console.warn('Button.Icon: plain is deprecated. Use variant="plain" instead.')
}
